import { useMemo, useState, useEffect } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_TableOptions,
} from 'material-react-table';
import {
  Typography,
  TextField,
  Box,
  InputAdornment,
  useMediaQuery,
  Pagination,
  IconButton,
} from '@mui/material';
import { MRT_Cell } from 'material-react-table';
import searchIcon from '../../../../assets/icons/Icon.svg';
import add from '../../../../assets/icons/PlusWhiteStroke.svg';
import DeleteIcon from '../../../../assets/icons/delete1.svg';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import { useNavigate } from 'react-router-dom';
import ROUTE from 'packages/novous_web/src/router/Constant';
import ChooseCompanyModal from './ChooseCompanyModal';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import AlertToast from 'packages/novous_web/src/common/components/Alert';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';

interface User {
  id: string;
  authority: string;
  userName: string;
  name: string;
  company: string;
  head: string;
  email: string;
  mobile: string;
}

type ColumnObjType = {
  [key: string]: {
    name: string;
    status: boolean;
  };
};

export const UserScreen: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [data, setData] = useState<User[]>([]);
  const [filteredData, setFilteredData] = useState<User[]>([]);
  const [paginatedData, setPaginatedData] = useState<User[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const { user } = useAuth();
  const [selectedCompanyData, setSelectedCompanyData] = useState<{
    name: string;
    crn: string;
    id: string;
  } | null>(null);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const isSmallScreen = useMediaQuery('(max-width:850px)');
  const navigate = useNavigate();

  const rowsPerPage = 10;

  // API Hook for fetching customer list by CRN
  const {
    data: customerListByCrn,
    error: customerListByCrnError,
    loading: customerListByCrnLoading,
    fetchData: fetchCustomerListByCrn,
  } = useAxios<any>(`${API_ENDPOINTS.users.CUSTOMER_LIST_CRN}`, 'POST');

  // API Hook for deleting user
  const {
    data: deleteUserData,
    error: deleteUserError,
    loading: deleteUserLoading,
    fetchData: fetchDeleteUser,
  } = useAxios<any>(`${API_ENDPOINTS.users.USER_ACTIVE_DELETE}`, 'POST');

  const [columnObj, setColumnObj] = useState<ColumnObjType>({
    authority: { name: 'Authority', status: true },
    userName: { name: 'UserName', status: true },
    name: { name: 'Name', status: true },
    company: { name: 'Company', status: true },
    head: { name: 'Head', status: true },
    email: { name: 'Email', status: true },
    mobile: { name: 'Mobile', status: true },
    actions: { name: 'Action', status: true },
  });

  // Console log customer list data when it's received
  useEffect(() => {
    if (customerListByCrn && !customerListByCrnLoading) {
      console.log('Customer List by CRN API Response:', customerListByCrn);

      // The API response is wrapped in an object with 'data' property
      const apiData = customerListByCrn.data || customerListByCrn;
      console.log('Extracted API Data:', apiData);
      console.log('Is Array:', Array.isArray(apiData));
      console.log('Length:', apiData?.length);

      // Transform API data to match our User interface
      if (Array.isArray(apiData)) {
        const transformedData: User[] = apiData.map((item: any) => ({
          id: item.id,
          authority: item.authorities || '',
          userName: item.username || '',
          name: item.fullName || `${item.firstName || ''} ${item.lastName || ''}`.trim(),
          company: item.companyName || '',
          head: item.parentName || '',
          email: item.email || '',
          mobile: item.phone || '',
        }));

        console.log('Setting user data:', transformedData);
        setData(transformedData);
        setFilteredData(transformedData);
        console.log('Transformed User Data length:', transformedData.length);
      } else {
        console.log('Customer data is not an array:', typeof apiData);
      }
    }
  }, [customerListByCrn, customerListByCrnLoading]);

  // Console log any errors from the API
  useEffect(() => {
    if (customerListByCrnError) {
      console.error('Customer List by CRN API Error:', customerListByCrnError);
    }
  }, [customerListByCrnError]);

  // Handle delete user response
  useEffect(() => {
    if (deleteUserData && !deleteUserLoading) {
      console.log('Delete User API Response:', deleteUserData);

      // Show success toast with the message from API response
      if (deleteUserData.message) {
        setToast({
          open: true,
          message: deleteUserData.message,
          type: 'success',
        });
      }

      // Refresh the user list after successful deletion
      if (selectedCompanyData) {
        fetchCustomerListByCrn({
          userCrn: selectedCompanyData.crn,
        });
      }
    }
  }, [deleteUserData, deleteUserLoading]);

  // Handle delete user errors
  useEffect(() => {
    if (deleteUserError) {
      console.error('Delete User API Error:', deleteUserError);
      setToast({
        open: true,
        message: deleteUserError || 'Failed to delete user',
        type: 'error',
      });
    }
  }, [deleteUserError]);

  // Add search filtering effect
  useEffect(() => {
    console.log('Filtering user data with search query:', searchQuery);
    console.log('Current data length:', data.length);

    if (searchQuery.trim() === '') {
      setFilteredData(data);
    } else {
      const filtered = data.filter(
        (user) =>
          user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.mobile.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.authority.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.company.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setFilteredData(filtered);
    }

    // Reset to first page when search changes
    setCurrentPage(1);
    console.log('Filtered user data length:', filteredData.length);
  }, [searchQuery, data]);

  // Pagination effect
  useEffect(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginated = filteredData.slice(startIndex, endIndex);
    setPaginatedData(paginated);
    console.log(
      `Showing page ${currentPage}, rows ${startIndex + 1}-${Math.min(endIndex, filteredData.length)} of ${
        filteredData.length
      }`,
    );
  }, [filteredData, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    console.log('Page changed to:', page);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>, user: User) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleDeleteUser = (user: User) => {
    console.log('Deleting user:', user);

    // Call USER_ACTIVE_DELETE API with the user's ID as userId
    fetchDeleteUser({
      userId: user.id,
    });

    // Close any open menu
    setAnchorEl(null);
    setSelectedUser(null);
  };

  useEffect(() => {
    // If role is SUPER_ADMIN, wait for company selection; if ADMIN, fetch directly
    if (user?.authorities === 'ROLE_SUPER_ADMIN') {
      // For SUPER_ADMIN, only fetch if a company is selected
      if (selectedCompanyData) {
        fetchCustomerListByCrn({
          userCrn: selectedCompanyData.crn,
        });
      }
    } else {
      // For ADMIN, fetch using their own CRN
      if (user?.crn) {
        fetchCustomerListByCrn({
          userCrn: user?.crn,
        });
      }
    }
  }, [user, selectedCompanyData]);

  const handleCompanySelect = (companyData: { name: string; crn: string; id: string }) => {
    setSelectedCompanyData(companyData);
    console.log('Selected company data:', companyData);

    // Call CUSTOMER_LIST_CRN API with the selected company's CRN
    fetchCustomerListByCrn({
      userCrn: companyData.crn,
    });
  };

  const handleOpenCompanyModal = () => {
    setCompanyModalOpen(true);
  };

  const handleCloseCompanyModal = () => {
    setCompanyModalOpen(false);
  };

  const allColumns: MRT_ColumnDef<User>[] = useMemo(() => {
    return [
      {
        accessorKey: 'authority',
        header: 'Authority',
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 180,
        muiTableBodyCellProps: {
          sx: { maxWidth: '180px', padding: '8px', overflow: 'hidden' },
        },
        Cell: ({ cell }: { cell: MRT_Cell<User> }) => {
          const value = cell.getValue<string>();
          // Clean up the authority text
          const displayValue = value?.replace('ROLE_', '') || '';

          return (
            <Typography className="truncate" variant="body2" fontWeight={400} noWrap title={value}>
              {displayValue}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'userName',
        header: 'UserName',
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 180,
        muiTableBodyCellProps: {
          sx: { maxWidth: '180px', padding: '8px', overflow: 'hidden' },
        },
        Cell: ({ cell }: { cell: MRT_Cell<User> }) => (
          <Typography className="truncate" variant="body2" fontWeight={400} noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 200,
        muiTableBodyCellProps: {
          sx: { maxWidth: '200px', padding: '8px', overflow: 'hidden' },
        },
        Cell: ({ cell }: { cell: MRT_Cell<User> }) => (
          <Typography className="truncate" variant="body2" fontWeight={400} noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'company',
        header: 'Company',
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 200,
        muiTableBodyCellProps: {
          sx: { maxWidth: '200px', padding: '8px', overflow: 'hidden' },
        },
        Cell: ({ cell }: { cell: MRT_Cell<User> }) => (
          <Typography className="truncate" variant="body2" fontWeight={400} noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'head',
        header: 'Head',
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 200,
        muiTableBodyCellProps: {
          sx: { maxWidth: '200px', padding: '8px', overflow: 'hidden' },
        },
        Cell: ({ cell }: { cell: MRT_Cell<User> }) => (
          <Typography className="truncate" variant="body2" fontWeight={400} noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 200,
        muiTableBodyCellProps: {
          sx: { maxWidth: '200px', padding: '8px', overflow: 'hidden' },
        },
        Cell: ({ cell }: { cell: MRT_Cell<User> }) => (
          <Typography className="truncate" variant="body2" fontWeight={400} noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'mobile',
        header: 'Mobile',
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 200,
        muiTableBodyCellProps: {
          sx: { maxWidth: '200px', padding: '8px', overflow: 'hidden' },
        },
        Cell: ({ cell }: { cell: MRT_Cell<User> }) => (
          <Typography className="truncate" variant="body2" fontWeight={400} noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      // {
      //   accessorKey: 'actions',
      //   header: 'Action',
      //   enableColumnActions: false,
      //   size: isSmallScreen ? 90 : 140,
      //   muiTableBodyCellProps: {
      //     sx: { maxWidth: '140px', padding: '8px', overflow: 'hidden' },
      //   },
      //   Cell: ({ row }: { row: MRT_Cell<User>['row'] }) => (
      //     <div style={{ display: 'flex', alignItems: 'center' }}>
      //       <IconButton
      //         onClick={(e) => {
      //           e.stopPropagation();
      //           handleDeleteUser(row.original);
      //         }}
      //         disabled={deleteUserLoading}
      //       >
      //         <img
      //           src={DeleteIcon}
      //           alt="Delete"
      //           width={24}
      //           height={24}
      //           style={{ cursor: 'pointer', opacity: deleteUserLoading ? 0.5 : 1 }}
      //         />
      //       </IconButton>
      //     </div>
      //   ),
      // },
    ].filter((column) => columnObj[column.accessorKey as keyof ColumnObjType]?.status);
  }, [columnObj, isSmallScreen]);

  const columns: MRT_ColumnDef<User>[] = useMemo(() => {
    return allColumns.filter((column) => {
      const key = column.accessorKey as keyof ColumnObjType;
      return columnObj[key]?.status;
    });
  }, [allColumns, columnObj]);

  const tableOptions: MRT_TableOptions<User> = {
    columns,
    data: paginatedData,
    enablePagination: false,
    enableColumnFilters: false,
    enableSorting: false,
    enableGlobalFilter: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    enableFullScreenToggle: false,
    enableColumnResizing: false,
    enableToolbarInternalActions: false,
    // Show custom message when no data
    renderEmptyRowsFallback: () => (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px" width="100%">
        <Typography variant="h6" color="text.secondary">
          {customerListByCrnLoading
            ? 'Loading users...'
            : selectedCompanyData
            ? 'No users found for this company'
            : 'Please select a company to view users'}
        </Typography>
      </Box>
    ),
    muiTableBodyProps: {
      sx: {
        padding: 0,
        margin: 0,
      },
    },
    muiTableBodyRowProps: ({ staticRowIndex }) => ({
      sx: {
        backgroundColor: staticRowIndex % 2 === 0 ? '#F0EFF8' : 'inherit',
      },
    }),
  };

  const table = useMaterialReactTable(tableOptions);

  return (
    <>
      <div className="flex flex-col gap-4 w-full">
        <div className="flex justify-between items-center">
          <div className="flex flex-col gap-1">
            <Typography variant="h6">User Lists</Typography>
          </div>
          {user.authorities === 'ROLE_SUPER_ADMIN' && (
            <div className="flex gap-2">
              <CustomIconButton text="Select Company" variant="contained" onClick={handleOpenCompanyModal} />
            </div>
          )}
        </div>

        <div className="flex justify-between gap-4 sm:flex-row flex-col">
          <div className="flex gap-2">
            <div className="max-w-[400px]">
              <TextField
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search"
                sx={{
                  width: '400px',
                  height: '48px',
                  gap: '12px',
                  borderRadius: '8px',
                  borderWidth: '1px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    height: '48px',
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <img src={searchIcon} alt="search" width={16} height={16} />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          </div>

          <div className="flex gap-4 flex-wrap">
            <CustomIconButton
              icon={<img src={add} alt="Add" width={16} height={16} />}
              text="Create User"
              variant="contained"
              onClick={() => navigate(ROUTE.CreateUser)}
            />
          </div>
        </div>

        <MaterialReactTable table={table} />

        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            shape="rounded"
            color="primary"
            siblingCount={1}
            boundaryCount={1}
          />
        </Box>
      </div>

      <ChooseCompanyModal
        open={companyModalOpen}
        onClose={handleCloseCompanyModal}
        onCompanySelect={handleCompanySelect}
        selectedCompany={selectedCompanyData}
      />

      <AlertToast
        open={toast.open}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        type={toast.type as any}
      />
    </>
  );
};

export default UserScreen;
